import React from 'react';
import { motion } from 'framer-motion';

const HomeDecorShoppingRemorseLanding = ({ goToInput, theme = "mint" }) => {
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8">
      {/* SEO-optimized heading structure */}
      <h1 className="text-3xl font-bold text-center mb-6 dark:text-white">
        Home Decor Shopping Remorse: Understand Your Decorating Decisions
      </h1>
      
      {/* Hero section with main CTA */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-6 mb-8">
        <div className="flex flex-col md:flex-row items-center">
          <div className="mb-6 md:mb-0 md:mr-6 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-3 dark:text-white">Calculate Your Home Decor Guilt Score</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Track your home decorating purchases, understand your interior design spending habits, and reduce buyer's remorse.
            </p>
            <motion.button
              onClick={goToInput}
              className={`px-6 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Track Your Home Decor Purchase
            </motion.button>
          </div>
          <div className="text-8xl">🏠</div>
        </div>
      </div>
      
      {/* What causes home decor shopping remorse section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Why Do We Feel Guilty About Home Decor Purchases?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">💸</span>
              <div>
                <h3 className="font-bold dark:text-white">Budget Overruns</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Home decor projects often exceed planned budgets, leading to financial guilt.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">😔</span>
              <div>
                <h3 className="font-bold dark:text-white">Trend-Chasing</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Buying trendy items that quickly feel dated or mismatched with your home's aesthetic.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🛋️</span>
              <div>
                <h3 className="font-bold dark:text-white">Space Limitations</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Purchasing items without measuring or considering your actual living space.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🌍</span>
              <div>
                <h3 className="font-bold dark:text-white">Sustainability Concerns</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Environmental guilt from fast furniture and disposable home goods.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* How Our Tool Helps */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Our Home Decor Guilt Calculator Helps</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">1</span>
              Track Decorating Impulses
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Identify emotional triggers that lead to unplanned home decor purchases.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">2</span>
              Calculate Value Per Item
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Assess the long-term value and functionality of home decor investments.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">3</span>
              Get Personalized Design Advice
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Receive tailored recommendations for building a cohesive, guilt-free home.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">4</span>
              Create a Sustainable Home Plan
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Develop a more intentional approach to decorating that balances aesthetics and ethics.
            </p>
          </div>
        </div>
      </section>
      
      {/* Home Decor Shopper Types Section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Discover Your Home Decor Shopping Personality</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🛍️</span>
              <h3 className="font-bold mt-2 dark:text-white">The Seasonal Refresher</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You love updating your home with each season, resulting in storage spaces full of holiday decor and themed items.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">✨</span>
              <h3 className="font-bold mt-2 dark:text-white">The Pinterest Perfectionist</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You chase magazine-worthy aesthetics but often feel disappointed when your home doesn't match your vision board.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🔄</span>
              <h3 className="font-bold mt-2 dark:text-white">The Serial Redecorator</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You're constantly rearranging and replacing decor, never quite feeling settled with your current setup.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🧠</span>
              <h3 className="font-bold mt-2 dark:text-white">The Mindful Curator</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You carefully select timeless pieces that tell a story and create a home that evolves slowly and intentionally.
            </p>
          </div>
        </div>
      </section>
      
      {/* Home Decor Guilt Stats */}
      <section className="mb-8 bg-gray-50 dark:bg-gray-700 rounded-xl p-5">
        <h2 className="text-xl font-bold mb-4 dark:text-white">Home Decorating By The Numbers</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold dark:text-white">$8,000+</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">average spent on home furnishings annually</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">68%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">of decorators exceed their initial budget</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">3-7yrs</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">average lifespan of trendy decor items</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">42%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">experience post-purchase regret</p>
          </div>
        </div>
      </section>
      
      {/* Testimonials section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Others Improved Their Home Decor Choices</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "After tracking my home purchases for two months, I realized I'd spent over $800 on throw pillows alone! Now I have a 'one in, one out' policy and focus on quality over quantity."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Alex J.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Reformed Seasonal Refresher</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "I used to buy decor impulsively without measuring my space first. The guilt calculator helped me create a home plan and now everything I buy has a purpose and a place."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Sam K.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Mindful Curator</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section - important for SEO */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Common Questions About Home Decor Shopping Remorse</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Why do I feel guilty after buying home decor?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Home decor guilt often stems from spending more than planned, buying trendy items that don't integrate well with your existing decor, concerns about waste and sustainability, or feeling like your home still doesn't match your vision despite the purchases.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How can I create a cohesive home without overspending?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Start by tracking your current purchasing patterns with our guilt calculator. Then focus on creating a design plan before shopping, selecting a consistent color palette, investing in versatile foundational pieces, and adding distinctive items gradually over time instead of all at once.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">What are some sustainable home decorating practices?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Consider buying secondhand furniture, repurposing or upcycling existing items, choosing natural materials that age well, supporting local artisans for unique pieces, and selecting quality items designed to last decades rather than seasons.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How do I stop impulsively buying home decor?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Track your purchases with our home decor guilt calculator to identify triggers. Implement strategies like a 30-day waiting period for non-essential items, creating a home vision board to evaluate if new items truly fit, and setting a dedicated quarterly decorating budget instead of making spontaneous purchases.
            </p>
          </div>
        </div>
      </section>
      
      {/* CTA section */}
      <section className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-xl p-6 text-center">
        <h2 className="text-2xl font-bold mb-3 dark:text-white">Ready to Create a Home You Love Without Regrets?</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          Start tracking your home decor purchases and transform your approach to decorating.
        </p>
        <motion.button
          onClick={goToInput}
          className={`px-8 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Calculate Your Home Decor Guilt Now
        </motion.button>
      </section>
      
      {/* Rich SEO footer content */}
      <footer className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">About Home Decor Shopping Remorse</h2>
          <p className="mb-3">
            Home decor shopping remorse is the feeling of regret experienced after purchasing items for your living space. This can stem from budget concerns, realizing an item doesn't fit your space physically or aesthetically, or concerns about sustainability and excessive consumption.
          </p>
          <p className="mb-3">
            Our Home Decor Guilt Calculator helps you understand and address these feelings by tracking your emotional state during purchases, analyzing your decorating patterns, and providing personalized strategies to create a home you truly love without the regret.
          </p>
          <p>
            Whether you're dealing with impulse buying, trend-chasing, or space planning challenges, our free tool helps you develop more intentional decorating habits and reduce shopping guilt as you create your perfect home environment.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HomeDecorShoppingRemorseLanding;
